import { BudgetPermissions, useBudgetContext } from 'hooks'
import { useMemo } from 'react'
import { BudgetStatus, type BaseBudget, type BudgetIds } from 'typings'

export enum BudgetViewState {
  EMPTY = 'empty',
  ACTIVE = 'active',
  DISABLED_DISCOUNT_TYPE = 'disabled-discount-type',
  DISABLED_BUDGET = 'disabled-budget',
  LOADING = 'loading',
  INVALID = 'invalid',
  UNAUTHORIZED = 'unauthorized',
}

type UseBudgetViewStateProps = {
  baseBudget: BaseBudget | undefined
  budgetIds: BudgetIds | undefined
  isBudgetComplete: boolean
  isAllowedDiscountType: boolean
  isLoading?: boolean
}

export function useBudgetViewState(
  props: UseBudgetViewStateProps
): BudgetViewState {
  const {
    baseBudget,
    budgetIds,
    isBudgetComplete,
    isAllowedDiscountType,
    isLoading = false,
  } = props

  const { permission } = useBudgetContext()

  const viewState = useMemo(() => {
    if (isLoading) {
      return BudgetViewState.LOADING
    }

    if (permission === BudgetPermissions.NONE) {
      return BudgetViewState.UNAUTHORIZED
    }

    if (!baseBudget && !budgetIds) {
      return BudgetViewState.EMPTY
    }

    if (budgetIds && !isBudgetComplete) {
      return BudgetViewState.INVALID
    }

    if (!isAllowedDiscountType) {
      return BudgetViewState.DISABLED_DISCOUNT_TYPE
    }

    if (baseBudget?.status === BudgetStatus.INACTIVE) {
      return BudgetViewState.DISABLED_BUDGET
    }

    return BudgetViewState.ACTIVE
  }, [
    baseBudget,
    budgetIds,
    isBudgetComplete,
    isAllowedDiscountType,
    isLoading,
    permission,
  ])

  return viewState
}
